import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { TIME_FORMAT } from 'src/configs/common.config';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionButton, PermissionGuard } from 'src/routes/components';
import ErrorPage403 from 'src/pages/error/403';
import {
  AllowlistEntry,
  approveAllowlistRequest,
  cancelAllowlistRequest,
  listAllowlistEntries,
  listAllowlistRequests,
  rejectAllowlistRequest,
  requestAllowlistChange,
} from '../../../services/financeTransferService';
import { RequestsTable } from '../../money-requests/RequestsTable';
import { useCurrentUserId, useRequestList } from '../../money-requests/useMoneyRequests';

const CHAIN = 'SOLANA';

const View = () => {
  const t = useTranslate();
  const notify = useNotify();
  const currentUserId = useCurrentUserId();
  const requests = useRequestList(listAllowlistRequests);
  const [entries, setEntries] = useState<AllowlistEntry[]>([]);
  const [activeOnly, setActiveOnly] = useState(true);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [adding, setAdding] = useState(false);
  const [address, setAddress] = useState('');
  const [label, setLabel] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const loadEntries = useCallback(async () => {
    setLoadingEntries(true);
    try {
      const res = await listAllowlistEntries({ size: 100, active: activeOnly ? true : undefined });
      setEntries(res.data ?? []);
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setLoadingEntries(false);
    }
  }, [activeOnly, notify]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const reloadAll = () => {
    loadEntries();
    requests.load();
  };

  const submit = async (action: 'ADD' | 'REMOVE', addr: string, lbl?: string, why?: string) => {
    setSaving(true);
    try {
      await requestAllowlistChange({
        action,
        chain: CHAIN,
        address: addr.trim(),
        label: lbl?.trim() || undefined,
        reason: why?.trim() || undefined,
      });
      notify(
        t('module.financeAllowlist.requested', {
          _: 'Request created; another admin must approve it.',
        }),
        {
          type: 'success',
        }
      );
      setAdding(false);
      setAddress('');
      setLabel('');
      setReason('');
      requests.load();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PermissionGuard resource="finance-allowlist" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs heading={t('resource.financeAllowlist', { _: 'Allowlist' })} />
        <Card sx={{ mt: 3, p: 2 }}>
          <Stack spacing={3}>
            <Alert severity="info" variant="outlined">
              {t('module.financeAllowlist.intro', {
                _: 'Transfers can only go to an address on this list. An empty list means no transfer is possible. An entry changes only when one admin requests it and a different admin approves it.',
              })}
            </Alert>

            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="h6">
                  {t('module.financeAllowlist.entries', { _: 'Addresses' })} ({CHAIN})
                </Typography>
                <Box sx={{ flex: 1 }} />
                <FormControlLabel
                  control={
                    <Switch
                      checked={activeOnly}
                      onChange={(e) => setActiveOnly(e.target.checked)}
                    />
                  }
                  label={t('module.financeAllowlist.activeOnly', { _: 'Active only' })}
                />
                <Button variant="outlined" onClick={reloadAll}>
                  {t('module.moneyRequests.reload', { _: 'Reload' })}
                </Button>
                <PermissionButton resource="finance-allowlist" action="request">
                  <Button variant="contained" onClick={() => setAdding(true)}>
                    {t('module.financeAllowlist.requestAdd', { _: 'Request addition' })}
                  </Button>
                </PermissionButton>
              </Stack>
              <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {t('module.financeAllowlist.address', { _: 'Address' })}
                      </TableCell>
                      <TableCell>{t('module.financeAllowlist.label', { _: 'Label' })}</TableCell>
                      <TableCell>{t('module.moneyRequests.status', { _: 'Status' })}</TableCell>
                      <TableCell>{t('module.moneyRequests.created', { _: 'Created' })}</TableCell>
                      <TableCell align="right">
                        {t('module.moneyRequests.actions', { _: 'Actions' })}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingEntries ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          {t('module.moneyRequests.loading', { _: 'Loading...' })}
                        </TableCell>
                      </TableRow>
                    ) : entries.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          {t('module.financeAllowlist.empty', {
                            _: 'No addresses: every transfer is refused.',
                          })}
                        </TableCell>
                      </TableRow>
                    ) : (
                      entries.map((en) => (
                        <TableRow key={en.id}>
                          <TableCell sx={{ wordBreak: 'break-all' }}>{en.address}</TableCell>
                          <TableCell>{en.label}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              variant="outlined"
                              color={en.active ? 'success' : 'default'}
                              label={en.active ? 'ACTIVE' : 'REMOVED'}
                            />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {en.createdAt ? dayjs(en.createdAt).format(TIME_FORMAT) : ''}
                          </TableCell>
                          <TableCell align="right">
                            {en.active ? (
                              <PermissionButton resource="finance-allowlist" action="request">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  disabled={saving}
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        t('module.financeAllowlist.removeConfirm', {
                                          _: 'Request removal of %{address}?',
                                          address: en.address,
                                        })
                                      )
                                    ) {
                                      submit('REMOVE', en.address);
                                    }
                                  }}
                                >
                                  {t('module.financeAllowlist.requestRemove', {
                                    _: 'Request removal',
                                  })}
                                </Button>
                              </PermissionButton>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>

            <Stack spacing={1}>
              <Typography variant="h6">
                {t('module.financeAllowlist.requests', { _: 'Change requests' })}
              </Typography>
              <RequestsTable
                rows={requests.rows}
                loading={requests.loading}
                currentUserId={currentUserId}
                busyId={requests.busyId}
                approvePermission={{ resource: 'finance-allowlist', action: 'approve' }}
                cancelPermission={{ resource: 'finance-allowlist', action: 'request' }}
                summary={(r) => (
                  <span>
                    <b>{r.payload.action}</b> {r.payload.address}
                    {r.payload.label ? ` (${r.payload.label})` : ''}
                  </span>
                )}
                onApprove={async (r) => {
                  await requests.run(
                    r.id,
                    () => approveAllowlistRequest(r.id),
                    t('module.moneyRequests.approved', { _: 'Approved' })
                  );
                  loadEntries();
                }}
                onReject={(r, why) =>
                  requests.run(
                    r.id,
                    () => rejectAllowlistRequest(r.id, why),
                    t('module.moneyRequests.rejected', { _: 'Rejected' })
                  )
                }
                onCancel={(r) =>
                  requests.run(
                    r.id,
                    () => cancelAllowlistRequest(r.id),
                    t('module.moneyRequests.cancelled', { _: 'Cancelled' })
                  )
                }
              />
            </Stack>
          </Stack>
        </Card>

        <Dialog open={adding} onClose={() => setAdding(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {t('module.financeAllowlist.requestAdd', { _: 'Request addition' })}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                size="small"
                required
                label={`${t('module.financeAllowlist.address', { _: 'Address' })} (${CHAIN})`}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                fullWidth
              />
              <TextField
                size="small"
                label={t('module.financeAllowlist.label', { _: 'Label' })}
                value={label}
                inputProps={{ maxLength: 100 }}
                onChange={(e) => setLabel(e.target.value)}
                fullWidth
              />
              <TextField
                size="small"
                label={t('module.moneyRequests.reason', { _: 'Reason' })}
                value={reason}
                inputProps={{ maxLength: 500 }}
                onChange={(e) => setReason(e.target.value)}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAdding(false)}>{t('actions.cancel')}</Button>
            <PermissionButton resource="finance-allowlist" action="request">
              <Button
                variant="contained"
                disabled={saving || !address.trim()}
                onClick={() => submit('ADD', address, label, reason)}
              >
                {t('module.financeAllowlist.submitRequest', { _: 'Submit request' })}
              </Button>
            </PermissionButton>
          </DialogActions>
        </Dialog>
      </DashboardContent>
    </PermissionGuard>
  );
};

export default View;
